import React, { useState } from 'react';
import { Send, Loader2, CheckCircle } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Textarea } from '@/components/ui/textarea';
import { Label } from '@/components/ui/label';
import { useSite } from '@/context/SiteContext';
import { toast } from 'sonner';

const ContactForm = () => {
  const { sendContactMessage } = useSite();
  const [loading, setLoading] = useState(false);
  const [submitted, setSubmitted] = useState(false);
  const [form, setForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });

  const handleChange = (field) => (e) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    
    if (!form.name.trim() || !form.email.trim() || !form.message.trim()) {
      toast.error('Please fill in all required fields');
      return;
    }

    setLoading(true);
    const success = await sendContactMessage(form);
    setLoading(false);

    if (success) {
      setSubmitted(true);
      toast.success('Message sent successfully!');
    } else {
      toast.error('Failed to send message. Please try again.');
    }
  };

  if (submitted) {
    return (
      <div className="text-center py-12">
        <div className="w-16 h-16 rounded-full bg-success/10 flex items-center justify-center mx-auto mb-4">
          <CheckCircle className="h-8 w-8 text-success" />
        </div>
        <h3 className="font-serif text-2xl mb-2">Thank You!</h3>
        <p className="text-muted-foreground mb-6">Your message has been sent. I'll get back to you soon.</p>
        <Button 
          variant="outline"
          onClick={() => {
            setSubmitted(false);
            setForm({ name: '', email: '', subject: '', message: '' });
          }}
        >
          Send Another Message
        </Button>
      </div>
    );
  }

  return (
    <form onSubmit={handleSubmit} className="space-y-6 max-w-xl mx-auto">
      <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
        <div className="space-y-2">
          <Label htmlFor="name">Name *</Label>
          <Input
            id="name"
            value={form.name}
            onChange={handleChange('name')}
            placeholder="Your name"
            className="bg-secondary border-border"
            required
          />
        </div>
        
        <div className="space-y-2">
          <Label htmlFor="email">Email *</Label>
          <Input
            id="email"
            type="email"
            value={form.email}
            onChange={handleChange('email')}
            placeholder="your@email.com"
            className="bg-secondary border-border"
            required
          />
        </div>
      </div>

      <div className="space-y-2">
        <Label htmlFor="subject">Subject</Label>
        <Input
          id="subject"
          value={form.subject}
          onChange={handleChange('subject')}
          placeholder="What is this regarding?"
          className="bg-secondary border-border"
        />
      </div>

      <div className="space-y-2">
        <Label htmlFor="message">Message *</Label>
        <Textarea
          id="message"
          value={form.message}
          onChange={handleChange('message')}
          placeholder="Your message..."
          rows={6}
          className="bg-secondary border-border resize-none"
          required
        />
      </div>

      <Button 
        type="submit" 
        className="w-full h-12 bg-foreground text-background hover:bg-foreground/90"
        disabled={loading}
      >
        {loading ? (
          <>
            <Loader2 className="h-4 w-4 mr-2 animate-spin" />
            Sending...
          </>
        ) : (
          <>
            <Send className="h-4 w-4 mr-2" />
            Send Message
          </>
        )}
      </Button>
    </form>
  );
};

export default ContactForm;
